import React from 'react';
import styled, { keyframes } from 'styled-components';
import motoImg from '../../assets/img/fast-shipping.svg';

/* ── keyframes ─────────────────────────────── */

const rideFlip = keyframes`
  0%   { transform: translateX(-90px) scaleX(1); }
  45%  { transform: translateX(90px) scaleX(1); }
  50%  { transform: translateX(90px) scaleX(-1); }
  95%  { transform: translateX(-90px) scaleX(-1); }
  100% { transform: translateX(-90px) scaleX(1); }
`;

const exhaustPuff = keyframes`
  0%   { opacity: 0.6; transform: translate(0, 0) scale(0.3); }
  60%  { opacity: 0.15; }
  100% { opacity: 0; transform: translate(-24px, -18px) scale(1.5); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 1; }
`;

const roadSlide = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(300%); }
`;

const bobble = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-2px); }
`;

/* ── styled ────────────────────────────────── */

const Overlay = styled.div`
  position: ${p => p.$inline ? 'relative' : 'fixed'};
  inset: ${p => p.$inline ? 'auto' : '0'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: ${p => p.$inline ? 'transparent' : 'linear-gradient(135deg, #fff 0%, #f0f4ff 100%)'};
  z-index: ${p => p.$inline ? 'auto' : '9999'};
  gap: ${p => p.$inline ? '16px' : '24px'};
  min-height: ${p => p.$inline ? '300px' : 'auto'};
  padding: ${p => p.$inline ? '40px 0' : '0'};
`;

const SceneWrap = styled.div`
  position: relative;
  width: 320px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MotoGroup = styled.div`
  animation: ${rideFlip} 3.4s ease-in-out infinite, ${bobble} 0.32s ease-in-out infinite;
  position: relative;
  display: flex;
  align-items: flex-end;
`;

const MotoImage = styled.img`
  width: 140px;
  height: auto;
  user-select: none;
  pointer-events: none;
  -webkit-user-drag: none;
`;

const Road = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 280px;
  height: 3px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 40%;
    height: 100%;
    background: repeating-linear-gradient(
      90deg,
      rgb(49, 140, 213) 0px,
      rgb(49, 140, 213) 8px,
      transparent 8px,
      transparent 16px
    );
    animation: ${roadSlide} 1.2s linear infinite;
  }
`;

const ExhaustCloud = styled.div`
  position: absolute;
  width: ${p => p.$s || 8}px;
  height: ${p => p.$s || 8}px;
  border-radius: 50%;
  background: rgba(160, 174, 192, 0.5);
  bottom: ${p => p.$b || 18}px;
  left: ${p => p.$l || -6}px;
  animation: ${exhaustPuff} ${p => p.$dur || '1s'} ease-out infinite;
  animation-delay: ${p => p.$del || '0s'};
`;

const DotsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(49, 140, 213);
  animation: ${pulse} 1.2s ease-in-out infinite;
  animation-delay: ${p => p.$delay || '0s'};
`;

const Label = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #64748b;
  letter-spacing: 0.3px;
  margin: 0;
`;

/* ── component ─────────────────────────────── */

const DeliveryLoading = ({ message, inline = false }) => {
  return (
    <Overlay $inline={inline}>
      <SceneWrap>
        <Road />

        <MotoGroup>
          {/* Fumaça do escapamento */}
          <ExhaustCloud $s={7}  $b={14} $l={-4}  $dur="0.9s"  $del="0s" />
          <ExhaustCloud $s={10} $b={20} $l={-10} $dur="1.1s"  $del="0.2s" />
          <ExhaustCloud $s={6}  $b={10} $l={0}   $dur="0.8s"  $del="0.45s" />
          <ExhaustCloud $s={12} $b={18} $l={-16} $dur="1.3s"  $del="0.1s" />
          <ExhaustCloud $s={8}  $b={24} $l={-8}  $dur="1s"    $del="0.35s" />

          <MotoImage src={motoImg} alt="Delivery" />
        </MotoGroup>
      </SceneWrap>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <Label>{message || 'Preparando tudo para você'}</Label>
        <DotsRow>
          <Dot $delay="0s" />
          <Dot $delay="0.2s" />
          <Dot $delay="0.4s" />
        </DotsRow>
      </div>
    </Overlay>
  );
};

export default DeliveryLoading;
